import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useNavigateBack } from '../../../hooks/useNavigateBack';
import useSWR from 'swr';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { formatDate as formatDateGlobal } from '../../../config/appConfig';
import { Accordion, AccordionItem, Button, Callout, Card, ChevronLeftIcon } from '../../../components/ui';
import { moduleConfig as caseConfig } from '../spare-cases/config';
import { moduleConfig as purchaseOrdersConfig } from '../spare-purchase-orders/sparePurchaseOrdersModuleConfig';
import { moduleConfig } from './quotationEvaluationsModuleConfig';
import { useQuotationEvaluation, type QuotationEvaluationSummary } from './useData';
import { fetcher } from '../../../services/fetcher';
import { supplyBlockedActionMessage } from '../blockingMessages';

const fmt2 = (n: string | number | null | undefined) => {
  if (n == null || n === '') return '';
  const v = typeof n === 'string' ? Number(n) : n;
  if (!Number.isFinite(v)) return '';
  return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);
};

type LineRow = {
  id: number;
  itemId?: number | null;
  itemVcode?: string | null;
  itemDescr?: string | null;
  qty?: number | null;
  minVal?: string | null;
  [key: string]: unknown;
};

const formatEvaluationItemCell = (row: LineRow) => {
  const v = (row.itemVcode ?? '').trim();
  const d = (row.itemDescr ?? '').trim();
  return [v, d].filter(Boolean).join(' - ');
};

const getEvaluationLevel1 = (row: LineRow): string => {
  const nested = row['item'] as { level1?: string | null } | null | undefined;
  const direct =
    (row['itemLevel1'] as string | null | undefined) ??
    (row['level1'] as string | null | undefined) ??
    nested?.level1;
  return (direct ?? '').trim();
};

const getEvaluationUnit = (row: LineRow): string => {
  const nested = row['unit'] as { ucode?: string | null } | null | undefined;
  const direct =
    (row['unitUcode'] as string | null | undefined) ??
    (row['ucode'] as string | null | undefined) ??
    nested?.ucode;
  return (direct ?? '').trim();
};

const Detail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { colors } = useTheme();
  const numericId = id && /^\d+$/.test(id) ? Number(id) : NaN;
  const { item, isLoading, isError } = useQuotationEvaluation(Number.isFinite(numericId) ? numericId : undefined);
  const { data: purchaseOrdersRes } = useSWR<any>('/spare-purchase-orders?page=1&limit=1000', fetcher);
  const [selectedBySlot, setSelectedBySlot] = React.useState<Record<number, Set<number>>>({});
  const getRowItemId = React.useCallback((row: LineRow): number | null => {
    const raw = row.itemId ?? (row['itemId'] as number | string | null | undefined);
    const n = typeof raw === 'string' ? Number(raw) : raw;
    return typeof n === 'number' && Number.isFinite(n) ? n : null;
  }, []);
  const summary = item?.summary as QuotationEvaluationSummary | undefined;
  const lines: LineRow[] = React.useMemo(() => item?.lines ?? [], [item?.lines]);
  const numCols = Math.min(5, summary?.byQuote?.length ?? 0);
  const rec = summary?.recommendedSlot;
  const recQuote = summary?.byQuote?.find((b) => b.slot === rec);
  const selectableItemIds = React.useMemo(
    () => lines.map((row) => getRowItemId(row)).filter((v): v is number => v != null),
    [lines, getRowItemId],
  );
  const slotSummary = React.useMemo(() => {
    const totals = new Map<number, { total: number; usd: number }>();
    for (let slot = 1; slot <= numCols; slot += 1) {
      totals.set(slot, { total: 0, usd: 0 });
    }
    for (const row of lines) {
      for (let slot = 1; slot <= numCols; slot += 1) {
        const bucket = totals.get(slot);
        if (!bucket) continue;
        const totalRaw = row[`quotation${slot}Total`] as string | number | null | undefined;
        const usdRaw = row[`quotation${slot}TotalUsd`] as string | number | null | undefined;
        const t = totalRaw == null || totalRaw === '' ? NaN : Number(totalRaw);
        const u = usdRaw == null || usdRaw === '' ? NaN : Number(usdRaw);
        if (Number.isFinite(t)) bucket.total += t;
        if (Number.isFinite(u)) bucket.usd += u;
      }
    }
    return totals;
  }, [lines, numCols]);

  const formatDate = (s?: string | null) => (s ? formatDateGlobal(s) : '');
  const backFallback =
    item != null && item.caseId != null
      ? caseConfig.module.route.caseView(item.caseId)
      : caseConfig.module.route.list;
  const goBack = useNavigateBack(backFallback);
  const currentCaseId = item?.caseId ?? 0;
  const hasOrderInCase = React.useMemo(() => {
    if (currentCaseId <= 0) return false;
    const rows = Array.isArray(purchaseOrdersRes) ? purchaseOrdersRes : purchaseOrdersRes?.data ?? [];
    return rows.some((po: any) => Number(po?.caseId) === currentCaseId);
  }, [purchaseOrdersRes, currentCaseId]);

  if (!Number.isFinite(numericId)) {
    return <p className={cn('text-sm', colors.textSecondary)}>Invalid id.</p>;
  }
  if (isLoading) {
    return <p className={cn('text-sm', colors.textSecondary)}>Loading…</p>;
  }
  if (isError || !item) {
    return (
      <div className="space-y-2">
        <p className={cn('text-sm', colors.textSecondary)}>This evaluation was not found.</p>
        <Button type="button" variant="ghost" onClick={goBack}>
          Back
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <Card
        variant="outlined"
        className={cn('flex flex-wrap items-center justify-between gap-3 px-4 py-3 sm:px-5', 'border shadow-sm')}
      >
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className={cn('-ml-2 gap-1.5', colors.text)}
            onClick={goBack}
          >
            <ChevronLeftIcon className="h-4 w-4" />
            Back
          </Button>
        </div>
        <div className="flex flex-wrap gap-2">
          {hasOrderInCase ? (
            <button
              type="button"
              disabled
              title="This evaluation cannot be edited because a purchase order exists in this spare case."
              className={cn(
                'inline-flex h-10 items-center justify-center rounded-lg px-4 text-sm font-medium',
                'bg-indigo-600 text-white opacity-60 cursor-not-allowed',
              )}
            >
              Edit
            </button>
          ) : (
            <Link
              to={moduleConfig.module.route.edit(item.id)}
              className={cn(
                'inline-flex h-10 items-center justify-center rounded-lg px-4 text-sm font-medium',
                'bg-indigo-600 text-white hover:opacity-90',
              )}
            >
              Edit
            </Link>
          )}
        </div>
      </Card>
      {hasOrderInCase ? (
        <Callout variant="danger" title="Cannot edit evaluation">{supplyBlockedActionMessage('QE_EDIT_CASE_HAS_ORDER')}</Callout>
      ) : null}

      {summary?.fxNote ? (
        <p className={cn('rounded-lg border p-3 text-sm', colors.border, colors.textSecondary)}>{summary.fxNote}</p>
      ) : null}

      {rec != null && recQuote ? (
        <Callout variant="success" title="Suggested for purchase order:">
          <span className="font-medium">
            Column {rec} - {recQuote.supplier?.name || `Supplier ${rec}`}
            {recQuote.supplier?.code ? ` (${recQuote.supplier.code})` : ''}
          </span>{' '}
          - {recQuote.ref || `Q-${rec}`} -{' '}
          {recQuote.grandTotalUsd != null
            ? `est. ${fmt2(recQuote.grandTotalUsd)} USD (lines + expenses)`
            : `${fmt2(recQuote.grandTotal)} ${recQuote.currency?.code ?? ''} total (native currency - verify FX)`}
        </Callout>
      ) : null}

      <Card variant="default" className="p-4 md:p-5">
        <Accordion className="space-y-3">
          <AccordionItem
            title="Evaluation details"
            description="Same order as edit: spare case, then evaluation date and reference, then notes."
            defaultOpen
            className={cn('overflow-hidden', colors.border, 'border')}
            headerClassName={cn('!gap-2', colors.surfaceHover)}
          >
            <div className="mt-2 pt-1 sm:pt-0">
              <dl className="grid grid-cols-1 gap-x-4 gap-y-4 text-sm xl:grid-cols-4">
                <div className="min-w-0">
                  <dt className={cn('text-xs font-medium uppercase tracking-wide', colors.textSecondary)}>Evaluation date</dt>
                  <dd className={cn('mt-1', colors.text)}>{formatDate(item.evaluationDate)}</dd>
                </div>
                <div className="min-w-0">
                  <dt className={cn('text-xs font-medium uppercase tracking-wide', colors.textSecondary)}>Spare case</dt>
                  <dd className={cn('mt-1 break-words', colors.text)}>
                    {item.spareCase?.id ? (
                      <Link
                        to={`/spares/spare-cases/${item.spareCase.id}/case-view`}
                        className="font-medium text-indigo-600 hover:underline dark:text-indigo-400"
                      >
                        {item.spareCase.ref || `Case #${item.spareCase.id}`}
                      </Link>
                    ) : item.caseId ? (
                      <span className="tabular-nums">#{item.caseId}</span>
                    ) : (
                      <span className={cn('italic', colors.textSecondary)}>—</span>
                    )}
                  </dd>
                </div>
                <div className="min-w-0">
                  <dt className={cn('text-xs font-medium uppercase tracking-wide', colors.textSecondary)}>Reference</dt>
                  <dd className={cn('mt-1 break-words font-medium tabular-nums', colors.text)}>
                    {item.ref || `Evaluation #${item.id}`}
                  </dd>
                </div>
                <div className="min-w-0">
                  <dt className={cn('text-xs font-medium uppercase tracking-wide', colors.textSecondary)}>Notes</dt>
                  <dd
                    className={cn(
                      'mt-1 min-h-[1.25rem] whitespace-pre-wrap break-words font-normal',
                      colors.text,
                    )}
                  >
                    {item.notes?.trim() ? item.notes : <span className={colors.textSecondary}>—</span>}
                  </dd>
                </div>
              </dl>
            </div>
          </AccordionItem>

          {summary && summary.byQuote.length > 0 ? (
            <AccordionItem
              title="Cost comparison"
              defaultOpen
              className={cn('overflow-hidden', colors.border, 'border')}
              headerClassName={cn('!gap-2', colors.surfaceHover)}
            >
              <p className={cn('mb-3 text-xs', colors.textSecondary)}>
                Line items plus expenses. Lowest estimated USD total is highlighted.
              </p>
              <div className="overflow-x-auto">
                <table className="min-w-[720px] w-full text-sm">
              <thead>
                <tr className={cn('border-b text-left', colors.border)}>
                  <th className="pb-2 pr-2 font-medium">Quote</th>
                  {summary.byQuote.map((b) => (
                    <th
                      key={b.slot}
                      className={cn('pb-2 px-2 font-medium', b.isLowestTotalUsd && 'text-emerald-700 dark:text-emerald-300')}
                    >
                      <div className="whitespace-nowrap">
                        {b.ref || `Q-${b.quotationId}`}
                        {b.isLowestTotalUsd ? <span className="ml-1 text-xs">★</span> : null}
                      </div>
                      <div className="text-xs font-normal opacity-80">
                        {b.supplier?.name ?? ''}
                        {b.currency?.code ? ` · ${b.currency.code}` : ''}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr className={cn('border-b', colors.border)}>
                  <th className="py-2 pr-2 text-left font-normal">Lines subtotal</th>
                  {summary.byQuote.map((b) => (
                    <td key={b.slot} className="px-2 py-2 tabular-nums">
                      {fmt2(b.lineItemsSubtotal)}
                    </td>
                  ))}
                </tr>
                <tr className={cn('border-b', colors.border)}>
                  <th className="py-2 pr-2 text-left font-normal">Expenses</th>
                  {summary.byQuote.map((b) => (
                    <td key={b.slot} className="px-2 py-2 tabular-nums">
                      {fmt2(b.expensesSubtotal)}
                    </td>
                  ))}
                </tr>
                <tr className={cn('border-b', colors.border, 'font-medium')}>
                  <th className="py-2 pr-2 text-left">Total (quote ccy)</th>
                  {summary.byQuote.map((b) => (
                    <td key={b.slot} className="px-2 py-2 tabular-nums">
                      {fmt2(b.grandTotal)}
                    </td>
                  ))}
                </tr>
                {summary.canCompareInUsd ? (
                  <tr className={cn('font-semibold', colors.text)}>
                    <th className="py-2 pr-2 text-left">Est. total (USD)</th>
                    {summary.byQuote.map((b) => (
                      <td
                        key={b.slot}
                        className={cn('px-2 py-2 tabular-nums', b.isLowestTotalUsd && 'text-emerald-700 dark:text-emerald-300')}
                      >
                        {b.grandTotalUsd != null ? fmt2(b.grandTotalUsd) : ''}
                      </td>
                    ))}
                  </tr>
                ) : (
                  <tr>
                    <td
                      colSpan={1 + summary.byQuote.length}
                      className={cn('pt-2 text-left text-xs', colors.textSecondary)}
                    >
                      Set the quotation rate on each non-USD quote to see comparable USD totals.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
            </AccordionItem>
          ) : null}

          {lines.length > 0 && numCols > 0 ? (
            <AccordionItem
              title="Line items"
              defaultOpen
              className={cn('overflow-hidden', colors.border, 'border')}
              headerClassName={cn('!gap-2', colors.surfaceHover)}
            >
              <div className={cn('mb-3 text-xs', colors.textSecondary)}>
                Select line items, then use <span className="font-medium">Direct PO</span> in the supplier column.
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-[900px] w-full text-sm">
              <thead>
                <tr className={cn('border-b', colors.border, colors.textSecondary, 'text-xs uppercase')}>
                  <th className="w-10 px-3 py-2 text-left tabular-nums" rowSpan={2}>#</th>
                  <th className="px-3 py-2 text-left" rowSpan={2}>Level 1</th>
                  <th className="px-3 py-2 text-left" rowSpan={2}>Item</th>
                  <th className="px-3 py-2 text-left" rowSpan={2}>Unit</th>
                  <th className="px-2 py-2 text-left" rowSpan={2}>Qty</th>
                  {Array.from({ length: numCols }, (_, i) => i + 1).map((slot) => {
                    const qm = (item as { quotes?: { id: number; ref?: string | null }[] }).quotes?.[slot - 1];
                    const byQuote = summary?.byQuote?.find((b) => b.slot === slot);
                    const quoteId = byQuote?.quotationId;
                    const supplierLabel =
                      byQuote?.supplier?.name?.trim() ||
                      byQuote?.supplier?.code?.trim() ||
                      `Supplier ${slot}`;
                    const selectedForSlot = selectedBySlot[slot] ?? new Set<number>();
                    const allCheckedForSlot =
                      selectableItemIds.length > 0 && selectableItemIds.every((id) => selectedForSlot.has(id));
                    const selectedItemIdsCsv = Array.from(selectedForSlot).sort((a, b) => a - b).join(',');
                    const poHref =
                      quoteId != null
                        ? `${purchaseOrdersConfig.module.route.new}?quotationId=${quoteId}&selectedItemIds=${encodeURIComponent(selectedItemIdsCsv)}`
                        : null;
                    const groupBg = slot % 2 === 0 ? 'bg-black/[0.02] dark:bg-white/[0.03]' : '';
                    return (
                      <th
                        key={slot}
                        colSpan={3}
                        className={cn(
                          'px-2 py-2 text-left whitespace-nowrap align-top',
                          'border-l border-gray-300/70 dark:border-gray-600/70',
                          groupBg,
                        )}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <div className={cn('truncate text-[0.75rem] font-semibold normal-case', colors.text)}>
                              {supplierLabel}
                            </div>
                            <div className={cn('mt-0.5 text-[0.65rem] font-normal normal-case', colors.textSecondary)}>
                              {qm?.ref || (qm?.id != null ? `#${qm.id}` : '')}
                            </div>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <label
                              className={cn(
                                'inline-flex h-7 items-center gap-1 rounded-md border px-2 text-[0.65rem] font-medium normal-case',
                                colors.border,
                              )}
                            >
                              <input
                                type="checkbox"
                                checked={allCheckedForSlot}
                                onChange={(e) => {
                                  const checked = e.target.checked;
                                  setSelectedBySlot((prev) => ({
                                    ...prev,
                                    [slot]: checked ? new Set(selectableItemIds) : new Set<number>(),
                                  }));
                                }}
                                aria-label={`Select all items in column ${slot}`}
                              />
                              All
                            </label>
                            {poHref ? (
                              <Link
                                to={poHref}
                                className={cn(
                                  'inline-flex h-7 items-center rounded-md border px-2 text-[0.65rem] font-semibold normal-case',
                                  selectedForSlot.size === 0
                                    ? 'pointer-events-none opacity-50'
                                    : 'hover:bg-emerald-50 dark:hover:bg-emerald-900/30',
                                  colors.border,
                                )}
                                aria-disabled={selectedForSlot.size === 0}
                                title={selectedForSlot.size === 0 ? 'Select item(s) first' : 'Create PO from selected items'}
                              >
                                Direct PO
                              </Link>
                            ) : null}
                          </div>
                        </div>
                      </th>
                    );
                  })}
                  <th className="px-2 py-2 text-right" rowSpan={2}>Min (row)</th>
                </tr>
                <tr className={cn('border-b', colors.border, colors.textSecondary, 'text-[0.65rem] uppercase')}>
                  {Array.from({ length: numCols }, (_, i) => i + 1).map((slot) => (
                    <React.Fragment key={`subhead-${slot}`}>
                      <th
                        className={cn(
                          'px-2 py-1 text-left',
                          'border-l border-gray-300/70 dark:border-gray-600/70',
                          slot % 2 === 0 ? 'bg-black/[0.02] dark:bg-white/[0.03]' : '',
                        )}
                      >
                        Select
                      </th>
                      <th className={cn('px-2 py-1 text-right', slot % 2 === 0 ? 'bg-black/[0.02] dark:bg-white/[0.03]' : '')}>Total</th>
                      <th className={cn('px-2 py-1 text-right', slot % 2 === 0 ? 'bg-black/[0.02] dark:bg-white/[0.03]' : '')}>USD</th>
                    </React.Fragment>
                  ))}
                </tr>
              </thead>
              <tbody>
                {lines.map((row, idx) => {
                  const canUsd = summary?.canCompareInUsd;
                  const mv = row.minVal != null && row.minVal !== '' ? Number(row.minVal) : null;
                  const rowItemId = getRowItemId(row);
                  return (
                  <tr key={row.id} className={cn('border-b', colors.border)}>
                    <td className="px-3 py-2 align-top tabular-nums">{idx + 1}</td>
                    <td className="px-3 py-2 align-top">
                      {getEvaluationLevel1(row)}
                    </td>
                    <td className={cn('px-3 py-2 align-top', colors.text)}>
                      {formatEvaluationItemCell(row)}
                    </td>
                    <td className={cn('px-3 py-2 align-top', colors.textSecondary)}>{getEvaluationUnit(row)}</td>
                    <td className="px-2 py-2 tabular-nums align-top">{row.qty != null ? row.qty : ''}</td>
                    {Array.from({ length: numCols }, (_, i) => i + 1).map((slot) => {
                      const total = row[`quotation${slot}Total`] as string | null | undefined;
                      const usd = row[`quotation${slot}TotalUsd`] as string | null | undefined;
                      const tNum = total != null && total !== '' ? Number(total) : null;
                      const uNum = usd != null && usd !== '' ? Number(usd) : null;
                      const isMin =
                        mv != null && Number.isFinite(mv) &&
                        (canUsd
                          ? uNum != null && Math.abs(uNum - mv) < 0.01
                          : tNum != null && Math.abs(tNum - mv) < 0.01);
                      return (
                        <React.Fragment key={slot}>
                          <td
                            className={cn(
                              'px-2 py-2 align-top',
                              'border-l border-gray-300/70 dark:border-gray-600/70',
                              slot % 2 === 0 ? 'bg-black/[0.02] dark:bg-white/[0.03]' : '',
                            )}
                          >
                            <input
                              type="checkbox"
                              checked={rowItemId != null && (selectedBySlot[slot]?.has(rowItemId) ?? false)}
                              disabled={rowItemId == null}
                              onChange={(e) => {
                                if (rowItemId == null) return;
                                const checked = e.target.checked;
                                setSelectedBySlot((prev) => {
                                  const nextSet = new Set(prev[slot] ?? []);
                                  if (checked) nextSet.add(rowItemId);
                                  else nextSet.delete(rowItemId);
                                  return { ...prev, [slot]: nextSet };
                                });
                              }}
                              aria-label={`Select ${row.itemVcode || row.itemDescr || `row ${row.id}`} for column ${slot}`}
                            />
                          </td>
                          <td
                            className={cn(
                              'px-2 py-2 tabular-nums align-top whitespace-nowrap text-right',
                              slot % 2 === 0 ? 'bg-black/[0.02] dark:bg-white/[0.03]' : '',
                            )}
                          >
                            <span className="inline-flex items-center justify-end gap-1">
                              {isMin ? (
                                <span
                                  className={cn('text-[0.7rem] font-semibold', colors.textSecondary)}
                                  title="Cheapest for this item"
                                  aria-label="Cheapest"
                                >
                                  ★
                                </span>
                              ) : null}
                              <span>{fmt2(total)}</span>
                            </span>
                          </td>
                          <td
                            className={cn(
                              'px-2 py-2 tabular-nums align-top whitespace-nowrap text-right text-xs',
                              colors.textSecondary,
                              slot % 2 === 0 ? 'bg-black/[0.02] dark:bg-white/[0.03]' : '',
                            )}
                          >
                            {summary?.canCompareInUsd ? fmt2(usd) : ''}
                          </td>
                        </React.Fragment>
                      );
                    })}
                    <td className="px-2 py-2 tabular-nums text-xs text-right align-top">{fmt2(row.minVal as string | undefined)}</td>
                  </tr>
                );
                })}
              </tbody>
              <tfoot>
                <tr className={cn('border-t', colors.border)}>
                  <th className={cn('px-3 py-2 text-left text-xs font-semibold', colors.textSecondary)} colSpan={5}>
                    Summary
                  </th>
                  {Array.from({ length: numCols }, (_, i) => i + 1).map((slot) => {
                    const s = slotSummary.get(slot) ?? { total: 0, usd: 0 };
                    return (
                      <React.Fragment key={`sum-${slot}`}>
                        <td
                          className={cn(
                            'px-2 py-2',
                            'border-l border-gray-300/70 dark:border-gray-600/70',
                            slot % 2 === 0 ? 'bg-black/[0.02] dark:bg-white/[0.03]' : '',
                          )}
                        />
                        <td
                          className={cn(
                            'px-2 py-2 text-right text-xs font-semibold tabular-nums',
                            slot % 2 === 0 ? 'bg-black/[0.02] dark:bg-white/[0.03]' : '',
                            colors.text,
                          )}
                        >
                          {fmt2(s.total)}
                        </td>
                        <td
                          className={cn(
                            'px-2 py-2 text-right text-xs font-semibold tabular-nums',
                            slot % 2 === 0 ? 'bg-black/[0.02] dark:bg-white/[0.03]' : '',
                            colors.textSecondary,
                          )}
                        >
                          {summary?.canCompareInUsd ? fmt2(s.usd) : ''}
                        </td>
                      </React.Fragment>
                    );
                  })}
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
            </AccordionItem>
          ) : null}
        </Accordion>
      </Card>
    </div>
  );
};

export default Detail;
